'use client'

import React, { useState } from 'react'
import { useRouter } from 'next/navigation'

import { useIsDesktop } from '@rewardops/ui'
import { EarnMessagingBannerContent } from '@rewardops/utils'

import { CloseBtn } from '~/components/contentful/banner-close-btn'
import MapleButton from '../../components/button'

/**
 * Wishlist Earn Banner - populated from Contentful
 * Content is managed in Contentful as a section in the 'wishlist' page
 * Supports:
 * - bannerTitle, bannerBody from Contentful
 * - bannerButton, learnMoreLink from Contentful
 * - image from Contentful Assets
 * - backgroundColor from Contentful color field
 * - Dismissible with X button
 */
type Props = {
  content: EarnMessagingBannerContent
}

const WishlistEarnBanner: React.FC<Props> = ({ content }: Props) => {
  const {
    backgroundColor,
    image,
    bannerTitle,
    bannerBody,
    bannerButton,
    learnMoreLink,
  } = content
  const { push } = useRouter()
  const isDesktop = useIsDesktop()
  const [isDismissed, setIsDismissed] = useState(false)

  if (isDismissed) return null

  const handleButtonClick = () => {
    if (!bannerButton?.to) return

    if (bannerButton.isRelativeUrl === false) {
      if (typeof window !== 'undefined') {
        window.open(bannerButton.to, '_blank', 'noopener,noreferrer')
      }
    } else {
      push(bannerButton.to)
    }
  }

  const handleLearnMoreClick = () => {
    if (!learnMoreLink?.to) return

    if (learnMoreLink.isRelativeUrl === false) {
      if (typeof window !== 'undefined') {
        window.open(learnMoreLink.to, '_blank', 'noopener,noreferrer')
      }
    } else {
      push(learnMoreLink.to)
    }
  }

  const hasImage = image
  const displayImage = image?.file?.url

  // Desktop layout - horizontal with image on left
  const desktopBanner = (
    <div
      className="relative flex h-[166px] w-full overflow-hidden rounded-lg"
      style={{ backgroundColor }}
    >
      <CloseBtn setIsClosed={setIsDismissed} />

      {hasImage && displayImage && (
        <div className="h-full flex-shrink-0" style={{ width: '274px' }}>
          <img
            src={displayImage}
            alt={image?.title ?? 'earn banner image'}
            className="h-full w-full rounded-l-lg object-cover"
          />
        </div>
      )}
      <div className="flex flex-1 items-center justify-between px-12 pr-16">
        <div className="flex max-w-[547px] flex-col gap-2">
          <h3 className="font-[Jost] text-[34px] font-semibold leading-[34px] tracking-[-0.2px] text-white">
            {bannerTitle}
          </h3>
          <p className="max-w-[443.13px] font-[Inter] text-base font-normal leading-[20px] text-white">
            {bannerBody}
          </p>
          {learnMoreLink?.name && (
            <button
              onClick={handleLearnMoreClick}
              className="cursor-pointer text-left text-sm font-medium text-white underline hover:no-underline"
            >
              {learnMoreLink.name}
            </button>
          )}
        </div>
        {bannerButton?.name && (
          <MapleButton
            id="wishlist-earn-button-desktop"
            buttonStyle="secondary"
            className="h-[56px] w-[187px] shrink-0 rounded-full bg-white px-4 font-[Jost] text-base font-semibold !normal-case leading-[23px] !tracking-normal tracking-[0px] text-black hover:bg-gray-100"
            onClick={handleButtonClick}
          >
            {bannerButton.name}
          </MapleButton>
        )}
      </div>
    </div>
  )

  // Mobile layout - vertical with image on top, center-aligned
  const mobileBanner = (
    <div>
      <div
        className="relative flex w-full flex-col overflow-hidden rounded-lg"
        style={{ backgroundColor }}
      >
        <CloseBtn setIsClosed={setIsDismissed} />

        {hasImage && displayImage && (
          <div className="h-[198px] w-full">
            <img
              src={displayImage}
              alt={image?.title ?? 'earn banner image'}
              className="h-full w-full rounded-t-lg object-cover"
            />
          </div>
        )}
        <div className="flex flex-col items-center px-3 py-6 text-center">
          <h3 className="mb-4 max-w-[296px] font-[Jost] text-[34px] font-semibold leading-[34px] tracking-[-0.2px] text-white">
            {bannerTitle}
          </h3>
          <p className="mb-4 max-w-[334px] font-[Inter] text-base font-normal leading-[20px] text-white">
            {bannerBody}
          </p>
          {learnMoreLink?.name && (
            <button
              onClick={handleLearnMoreClick}
              className="mb-4 cursor-pointer text-center text-sm font-medium text-white underline hover:no-underline"
            >
              {learnMoreLink.name}
            </button>
          )}
          {bannerButton?.name && (
            <MapleButton
              id="wishlist-earn-button-mobile"
              buttonStyle="secondary"
              className="h-[43.42px] rounded-[77.53px] bg-white px-8 font-[Jost] text-sm font-semibold !normal-case leading-[100%] !tracking-normal tracking-[0px] text-black hover:bg-gray-100"
              onClick={handleButtonClick}
            >
              {bannerButton.name}
            </MapleButton>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="col-span-2 mx-auto mb-[100px] w-full max-w-[1140px]">
      {isDesktop ? desktopBanner : mobileBanner}
    </div>
  )
}

export { WishlistEarnBanner }
